import MDEditor, { commands as EditorCommands, type ICommand } from '@uiw/react-md-editor';
import { useField } from 'formik';
import { memo, type ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { allowedTags } from '../../utilities/renderMarkdown';
import { type ContentRefinementProps, useRefineContentButton } from '../button/RefineContentButton';
import FormItem, { type FormItemProps } from './FormItem';
import '@uiw/react-md-editor/markdown-editor.css';

export type MarkdownFieldProps = ContentRefinementProps & {
    name: string;
    label: string;
    required: boolean;
    tooltip?: FormItemProps['tooltip'];
    addMoreButton?: ReactNode;
    commands?: ICommand[];
    disabled?: boolean;
};

export const StyledMarkdownWrapper = styled.div`
    textarea::selection {
        -webkit-text-fill-color: #fff;
    }
`;

export const StyledDiv = styled.div`
    display: flex;
`;

const MarkdownField = ({
    name,
    label,
    required,
    addMoreButton,
    tooltip,
    commands,
    withContentRefinement,
    disabled,
}: MarkdownFieldProps) => {
    const [field, meta, helpers] = useField(name);

    const refineContentButton = useRefineContentButton();

    const onChange = useCallback(
        (value: string): void => {
            helpers.setValue(value);
        },
        [helpers]
    );

    const editorCommands = useMemo(() => {
        if (disabled) {
            return [];
        }

        return (
            commands || [
                EditorCommands.bold,
                EditorCommands.italic,
                EditorCommands.strikethrough,
                EditorCommands.hr,
                EditorCommands.group([EditorCommands.title1, EditorCommands.title2], {
                    name: 'title',
                    groupName: 'title',
                    buttonProps: { 'aria-label': 'Insert title' },
                }),
                EditorCommands.divider,
                EditorCommands.link,
                EditorCommands.image,
                EditorCommands.divider,
                EditorCommands.unorderedListCommand,
                EditorCommands.orderedListCommand,
            ]
        );
    }, [commands, disabled]);

    return (
        <FormItem label={label} meta={meta} required={required} tooltip={tooltip}>
            <StyledMarkdownWrapper data-color-mode="light">
                <StyledDiv>
                    <MDEditor
                        commands={editorCommands}
                        onChange={onChange}
                        previewOptions={{
                            allowedElements: allowedTags,
                        }}
                        style={{ width: '100%' }}
                        textareaProps={{
                            disabled,
                        }}
                        value={field.value}
                    />
                    {addMoreButton}
                </StyledDiv>
                {withContentRefinement && refineContentButton.render({ name })}
            </StyledMarkdownWrapper>
        </FormItem>
    );
};

export default memo(MarkdownField);
